import { useState } from "react";
import { Pencil, EyeOff, Eye, MapPin } from "lucide-react";
import { ActionButton } from "@/components/ui/action-button";
import { TableRow, TableCell } from "@/components/ui/table";
import { VenueEditDialog } from "./VenueEditDialog";
import { useToggleVenueVisibility } from "@/hooks/useVenues";
import { toast } from "sonner";
import type { Venue } from "@/types";

interface VenueItemProps {
  venue: Venue;
}

export function VenueItem({ venue }: VenueItemProps) {
  const toggleVisibility = useToggleVenueVisibility();
  const [editOpen, setEditOpen] = useState(false);

  const handleToggle = async () => {
    try {
      await toggleVisibility.mutateAsync(venue.id);
      toast.success(venue.isHidden ? "Lokalizacja pokazana" : "Lokalizacja ukryta");
    } catch (err) {
      toast.error("Nie udało się zmienić widoczności", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      <TableRow className={venue.isHidden ? "opacity-60" : undefined}>
        <TableCell>
          <p className="font-medium text-sm">{venue.name}</p>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {venue.address ? <span className="truncate">{venue.address}</span> : "—"}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
          {venue.latitude != null && venue.longitude != null
            ? `${venue.latitude.toFixed(4)}, ${venue.longitude.toFixed(4)}`
            : "—"}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <ActionButton tooltip="Edytuj" className="h-8 w-8" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" />
            </ActionButton>
            {venue.latitude != null && venue.longitude != null && (
              <ActionButton
                asChild
                tooltip="Otwórz w Google Maps"
                className="h-8 w-8 text-muted-foreground"
              >
                <a
                  href={`https://www.google.com/maps?q=${venue.latitude},${venue.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin className="h-3.5 w-3.5" />
                </a>
              </ActionButton>
            )}
            <ActionButton
              tooltip={venue.isHidden ? "Pokaż lokalizację" : "Ukryj lokalizację"}
              className="h-8 w-8 text-muted-foreground"
              onClick={handleToggle}
              disabled={toggleVisibility.isPending}
            >
              {venue.isHidden ? (
                <Eye className="h-3.5 w-3.5" />
              ) : (
                <EyeOff className="h-3.5 w-3.5" />
              )}
            </ActionButton>
          </div>
        </TableCell>
      </TableRow>
      <VenueEditDialog venue={editOpen ? venue : null} open={editOpen} onOpenChange={setEditOpen} />
    </>
  );
}
